/**
 * Responsibility: the four numbers every payoff figure is computed from.
 *
 * **These used to be environment variables**, and they multiply through
 * every payoff on the discovery and catalogue boards: what grading a card
 * costs, what a submission costs to post, the marketplace's cut, and how
 * many cards go in a submission. Global, two people with different costs
 * were reading each other's arithmetic with no way to tell. They are per
 * person now, and this is where they are set.
 *
 * Money is text all the way through - typed as text, sent as text, stored
 * as text - because a fee rate of 0.13 through a float is not 0.13 coming
 * back. The same rule the rest of the system follows.
 *
 * Saving is explicit rather than on every keystroke. Each save recomputes
 * two boards, and a form that wrote as you type would recompute them per
 * character.
 */
import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hubApi, type UserSettingsInput } from '../../api/client';
import { useDurableSettings } from '../../api/hooks/useDurableSaved';

/** Blank while loading, so the fields do not flash the wrong numbers. */
const EMPTY: UserSettingsInput = {
  grading_cost_per_card: '',
  grading_submission_shipping: '',
  grading_sell_fee_rate: '',
  grading_cards_bought: 1,
};

export function GradingAssumptionsCard() {
  const queryClient = useQueryClient();
  const fromHub = useQuery({ queryKey: ['account', 'settings'], queryFn: hubApi.settings });
  const durable = useDurableSettings();
  // These four numbers drive every payoff figure on two boards. Reading them
  // from Firestore when the hub is unreachable is what keeps those pages
  // explicable rather than silently falling back to somebody else's defaults.
  const settings = {
    ...fromHub,
    data: fromHub.data ?? (fromHub.isError ? durable.data : undefined),
  };
  const [form, setForm] = useState<UserSettingsInput>(EMPTY);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!settings.data) return;
    setForm({
      grading_cost_per_card: settings.data.grading_cost_per_card,
      grading_submission_shipping: settings.data.grading_submission_shipping,
      grading_sell_fee_rate: settings.data.grading_sell_fee_rate,
      grading_cards_bought: settings.data.grading_cards_bought,
    });
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () => hubApi.saveSettings(form),
    onSuccess: async () => {
      setSaved(true);
      // Both boards are computed from these, so their cached answers are
      // now wrong. Cheaper to drop them than to reason about which.
      await queryClient.invalidateQueries({ queryKey: ['account', 'settings'] });
      await queryClient.invalidateQueries({ queryKey: ['catalogue'] });
      await queryClient.invalidateQueries({ queryKey: ['discovery'] });
    },
  });

  const money = (key: keyof UserSettingsInput) => ({
    value: String(form[key] ?? ''),
    onChange: (event: { target: { value: string } }) => {
      setSaved(false);
      // Digits and one point. Typed as text because it is stored as text.
      setForm({ ...form, [key]: event.target.value.replace(/[^0-9.]/g, '') });
    },
  });

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={2.5}>
        <Stack spacing={0.5}>
          <Typography variant="h6">Grading assumptions</Typography>
          <Typography variant="body2" color="text.secondary">
            Every payoff figure on the discovery and catalogue boards is computed
            from these. They are yours alone.
          </Typography>
        </Stack>

        {save.isError ? <Alert severity="error">{(save.error as Error).message}</Alert> : null}
        {saved ? <Alert severity="success">Saved. The boards will recompute.</Alert> : null}

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          }}
        >
          <TextField
            size="small"
            label="Grading cost per card"
            helperText="What PSA charges for one card, at your service level."
            {...money('grading_cost_per_card')}
          />
          <TextField
            size="small"
            label="Submission postage"
            helperText="For the whole submission, split across the cards in it."
            {...money('grading_submission_shipping')}
          />
          <TextField
            size="small"
            label="Selling fee rate"
            helperText="The marketplace's cut as a fraction. 0.13 is thirteen per cent."
            {...money('grading_sell_fee_rate')}
          />
          <TextField
            size="small"
            label="Cards per submission"
            helperText="How many you send at once. Postage divides by this."
            value={String(form.grading_cards_bought)}
            onChange={(event) => {
              setSaved(false);
              const digits = event.target.value.replace(/[^0-9]/g, '');
              setForm({ ...form, grading_cards_bought: Number(digits || 1) });
            }}
          />
        </Box>

        <Box>
          <Button
            variant="contained"
            disabled={save.isPending || settings.isLoading}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving' : 'Save'}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
}
